import React, { useState, useEffect } from 'react';
import { Clock, X, RefreshCw, AlertTriangle, CheckSquare, Square, Info } from 'lucide-react';
import { CustomTimePicker } from '../common/CustomTimePicker';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { easternOffsetMinutes } from '../../utils/easternTime';

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
  nickname: string;
  entry_id: number;
  clock_in: string;
}

interface BulkAdjustClockInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  job: {
    job_id: number;
    job_name: string;
    job_number: string;
  };
  employees: Employee[];
}

export const BulkAdjustClockInModal: React.FC<BulkAdjustClockInModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  job,
  employees
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(new Set());
  const [newClockInTime, setNewClockInTime] = useState('07:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEmployees(new Set(employees.map(e => e.employee_id)));
      setNewClockInTime('07:00');
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, employees]);

  const toggleEmployee = (employeeId: number) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedEmployees.size === employees.length) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(employees.map(e => e.employee_id)));
    }
  };

  const handleSubmit = async () => {
    if (selectedEmployees.size === 0) {
      setError('Please select at least one employee');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const employeeAdjustments = employees
        .filter(e => selectedEmployees.has(e.employee_id))
        .map(e => ({
          employee_id: e.employee_id,
          entry_id: e.entry_id
        }));

      const response = await api.post(API_CONFIG.ENDPOINTS.BULK_ADJUST_CLOCK_IN, {
        job_id: job.job_id,
        employee_adjustments: employeeAdjustments,
        new_clock_in_time: newClockInTime,
        // Eastern offset (not the viewer's) so the typed time means Eastern.
        timezone_offset_minutes: easternOffsetMinutes(new Date()),
        edit_reason: `Bulk adjusted clock-in to ${newClockInTime}`
      });

      if (response.data.success) {
        const data = response.data.data;
        let message = `Successfully adjusted ${data.adjusted_count} employee(s)`;
        if (data.prior_entries_closed > 0) {
          message += `. Auto-closed ${data.prior_entries_closed} prior entry(s).`;
        }
        if (data.errors && data.errors.length > 0) {
          message += ` ${data.errors.length} employee(s) had conflicts.`;
        }
        setSuccessMessage(message);

        // Close modal after short delay and refresh data
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.data?.errors && errorData.data.errors.length > 0) {
        const errorMessages = errorData.data.errors
          .map((e: any) => `${e.employee_name || `Employee ${e.employee_id}`}: ${e.error}`)
          .join('\n');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to adjust clock-in times');
      }
    } finally {
      setSaving(false);
    }
  };

  const formatClockIn = (clockIn: string) => {
    return new Date(clockIn).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen) return null;

  const styles = {
    modalBackdrop: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const
    },
    modalHeader: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    modalHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    modalIconWrapper: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: 'white',
      marginBottom: '4px'
    },
    modalSubtitle: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '14px'
    },
    modalCloseButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalBody: {
      padding: '20px',
      overflowY: 'auto' as const,
      flex: 1
    },
    selectAllRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      marginBottom: '12px',
      cursor: 'pointer',
      border: '1px solid #e5e7eb'
    },
    employeeList: {
      maxHeight: '200px',
      overflowY: 'auto' as const,
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    employeeRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px',
      borderBottom: '1px solid #f3f4f6',
      cursor: 'pointer'
    },
    employeeName: {
      flex: 1,
      fontWeight: '500',
      color: '#1f2937',
      fontSize: '14px'
    },
    clockInTime: {
      color: '#6b7280',
      fontSize: '13px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    infoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    infoText: {
      fontSize: '13px',
      color: '#1e40af'
    },
    errorBox: {
      padding: '12px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      marginBottom: '16px',
      color: '#dc2626',
      fontSize: '14px',
      whiteSpace: 'pre-wrap' as const
    },
    successBox: {
      padding: '12px',
      backgroundColor: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: '8px',
      marginBottom: '16px',
      color: '#16a34a',
      fontSize: '14px'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      padding: '16px 20px',
      paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb'
    },
    cancelButton: {
      flex: 1,
      padding: '14px 20px',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600'
    },
    confirmButton: {
      flex: 1,
      padding: '14px 20px',
      backgroundColor: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }
  };

  return (
    <div
      style={styles.modalBackdrop}
      onClick={onClose}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <div style={styles.modalHeaderLeft}>
            <div style={styles.modalIconWrapper}>
              <Clock size={24} color="white" />
            </div>
            <div>
              <h2 style={styles.modalTitle}>Adjust Clock-In Times</h2>
              <p style={styles.modalSubtitle}>{job.job_name}</p>
            </div>
          </div>
          <button
            style={styles.modalCloseButton}
            onClick={onClose}
          >
            <X size={20} color="white" />
          </button>
        </div>

        {/* Modal Body */}
        <div style={styles.modalBody}>
          {/* Error Message */}
          {error && (
            <div style={styles.errorBox}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div style={styles.successBox}>
              {successMessage}
            </div>
          )}

          {/* Select All */}
          <div
            style={styles.selectAllRow}
            onClick={toggleSelectAll}
          >
            {selectedEmployees.size === employees.length ? (
              <CheckSquare size={20} color="#f59e0b" />
            ) : (
              <Square size={20} color="#9ca3af" />
            )}
            <span style={{ fontWeight: '600', color: '#374151' }}>
              Select All ({employees.length} employee{employees.length !== 1 ? 's' : ''})
            </span>
          </div>

          {/* Employee List */}
          <div style={styles.employeeList}>
            {employees.map(emp => (
              <div
                key={emp.employee_id}
                style={styles.employeeRow}
                onClick={() => toggleEmployee(emp.employee_id)}
              >
                {selectedEmployees.has(emp.employee_id) ? (
                  <CheckSquare size={18} color="#f59e0b" />
                ) : (
                  <Square size={18} color="#9ca3af" />
                )}
                <span style={styles.employeeName}>
                  {emp.nickname || `${emp.first_name} ${emp.last_name}`}
                </span>
                <span style={styles.clockInTime}>
                  In: {formatClockIn(emp.clock_in)}
                </span>
              </div>
            ))}
          </div>

          {/* Time Picker */}
          <div style={styles.formGroup}>
            <label style={styles.label}>New Clock-In Time</label>
            <CustomTimePicker
              value={newClockInTime}
              onChange={(val) => setNewClockInTime(val)}
              label="Select New Clock-In Time"
            />
          </div>

          {/* Info Box */}
          <div style={styles.infoBox}>
            <Info size={16} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={styles.infoText}>
              Earlier entries will be auto-adjusted to end at the new clock-in time. If the new time is before an earlier entry starts, you'll need to delete that entry first.
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={styles.modalActions}>
          <button
            style={styles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.confirmButton,
              opacity: saving || selectedEmployees.size === 0 ? 0.6 : 1,
              cursor: saving || selectedEmployees.size === 0 ? 'not-allowed' : 'pointer'
            }}
            onClick={handleSubmit}
            disabled={saving || selectedEmployees.size === 0}
          >
            {saving ? (
              <>
                <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Adjusting...
              </>
            ) : (
              <>
                <Clock size={18} />
                Adjust {selectedEmployees.size}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAdjustClockInModal;
